// FILE: src/blueprint/state/blueprintReducer.js

import { ROUTES } from '../app/routes.js';
import { initialState } from './initialState.js';

export const ACTIONS = Object.freeze({
  SET_ROUTE: 'SET_ROUTE',
  SET_CONFIG: 'SET_CONFIG',
  SET_PACK: 'SET_PACK',

  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',

  SET_BLUEPRINT: 'SET_BLUEPRINT',
  SET_ACTIVE_MODULE_DOC: 'SET_ACTIVE_MODULE_DOC',

  OPEN_MODULE: 'OPEN_MODULE',
  CLOSE_MODULE: 'CLOSE_MODULE',

  NEXT_CONTENT: 'NEXT_CONTENT',
  PREV_CONTENT: 'PREV_CONTENT',
  GOTO_CONTENT: 'GOTO_CONTENT',

  SET_ANSWER: 'SET_ANSWER',
  SET_ANSWERS: 'SET_ANSWERS',
  SET_CURRENT_STEP_ID: 'SET_CURRENT_STEP_ID',
  TOGGLE_EXPLANATION_MODE: 'TOGGLE_EXPLANATION_MODE',

  SET_SELECTED_COACH: 'SET_SELECTED_COACH',
  SET_SELECTED_TYPING_MODE: 'SET_SELECTED_TYPING_MODE',
  SET_COACH_CONTEXT: 'SET_COACH_CONTEXT',

  OVERVIEW_HELP_LOADING: 'OVERVIEW_HELP_LOADING',
  OVERVIEW_HELP_SUCCESS: 'OVERVIEW_HELP_SUCCESS',
  OVERVIEW_HELP_ERROR: 'OVERVIEW_HELP_ERROR',

  INSIGHTS_LOADING: 'INSIGHTS_LOADING',
  INSIGHTS_SUCCESS: 'INSIGHTS_SUCCESS',
  INSIGHTS_ERROR: 'INSIGHTS_ERROR',
});

function clampInt(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

function getActiveContentCount(state) {
  const steps = state?.activeModuleDoc?.steps;
  const list = Array.isArray(steps) ? steps : [];
  return list.length;
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asString(value) {
  return typeof value === 'string' ? value : '';
}

function makeAnswerKey(packId, moduleId, trackId, stepId) {
  const p = String(packId || '').trim();
  const m = String(moduleId || '').trim();
  const t = String(trackId || '').trim();
  const s = String(stepId || '').trim();

  if (!p || !m || !t || !s) {
    return s;
  }

  return `${p}::${m}::${t}::${s}`;
}

function milestoneKey(milestone) {
  if (!milestone || typeof milestone !== 'object') return '';

  const id = typeof milestone.id === 'string' ? milestone.id.trim() : '';
  if (id) return id;

  const intent = typeof milestone.intent === 'string' ? milestone.intent.trim() : '';
  if (intent) return `intent:${intent}`;

  try {
    return `sig:${JSON.stringify(milestone)}`;
  } catch {
    return '';
  }
}

function normalizeBlock(block, entryId, index) {
  if (!block || typeof block !== 'object') return null;

  const type = asString(block.type).trim() || 'text';
  const title = asString(block.title).trim();
  const format = asString(block.format).trim() || 'plain';
  const className = asString(block.className).trim();

  if (type === 'choices') {
    const choices = asArray(block.choices)
      .map((choice) => {
        const safeChoice = asObject(choice);
        const id = asString(safeChoice.id).trim();
        const label = asString(safeChoice.label).trim();

        if (!id || !label) return null;

        return {
          ...safeChoice,
          id,
          label,
        };
      })
      .filter(Boolean);

    if (!choices.length) return null;

    return {
      ...block,
      id: asString(block.id).trim() || `${entryId}:block:${index}`,
      type,
      title,
      layout: asString(block.layout).trim() || 'stack',
      choices,
      className,
    };
  }

  if (type === 'icon') {
    const name = asString(block.name).trim();
    const label = asString(block.label).trim();

    if (!name) return null;

    return {
      ...block,
      id: asString(block.id).trim() || `${entryId}:block:${index}`,
      type,
      title,
      name,
      label,
      className,
    };
  }

  const body =
    typeof block.body === 'string'
      ? block.body.trim()
      : typeof block.text === 'string'
        ? block.text.trim()
        : typeof block.value === 'string'
          ? block.value.trim()
          : '';

  if (!title && !body) return null;

  return {
    ...block,
    id: asString(block.id).trim() || `${entryId}:block:${index}`,
    title,
    body,
    text: body,
    format,
    className,
  };
}

function normalizeEntry(entry, index, zoneName = '') {
  if (!entry || typeof entry !== 'object') return null;

  const placement = asObject(entry.placement);
  const entryId = asString(entry.id).trim() || `${zoneName || 'entry'}:${index}`;

  const blocks = asArray(entry.blocks)
    .map((block, blockIndex) => normalizeBlock(block, entryId, blockIndex))
    .filter(Boolean);

  const payload = asObject(entry.payload);
  const reportType = asString(entry.reportType).trim();
  const reportId = asString(entry.reportId).trim();
  const role = asString(entry.role).trim();
  const kind = asString(entry.kind).trim() || 'info';

  const hasRenderableBlocks = blocks.length > 0;
  const hasStructuredReportPayload =
    role === 'report' &&
    kind === 'info' &&
    (reportType !== '' || reportId !== '' || Object.keys(payload).length > 0);

  if (!hasRenderableBlocks && !hasStructuredReportPayload) {
    return null;
  }

  return {
    ...entry,
    id: entryId,
    sourceId: asString(entry.sourceId).trim(),
    kind,
    role,
    reportId,
    reportType,
    payload,
    assistantId: asString(entry.assistantId).trim(),
    zone: zoneName || asString(placement.zone).trim(),
    placement,
    blocks,
  };
}

function normalizeEntries(entries, zoneName = '') {
  return asArray(entries)
    .map((entry, index) => normalizeEntry(entry, index, zoneName))
    .filter(Boolean);
}

function normalizeLegacyGroups(groupsIn) {
  return asArray(groupsIn)
    .map((group) => {
      if (!group || typeof group !== 'object') return null;

      const cards = asArray(group.cards)
        .map((card, index) => {
          if (!card || typeof card !== 'object') return null;

          const title = asString(card.title).trim();
          const body =
            typeof card.body === 'string'
              ? card.body.trim()
              : typeof card.text === 'string'
                ? card.text.trim()
                : '';

          if (!title && !body) return null;

          return {
            ...card,
            id: asString(card.id).trim() || `legacy-card:${index}`,
            title,
            body,
            text: body,
            className: asString(card.className).trim(),
            format: asString(card.format).trim() || 'plain',
          };
        })
        .filter(Boolean);

      if (!cards.length) return null;

      return {
        ...group,
        id: asString(group.id).trim(),
        type: asString(group.type).trim() || 'plain',
        title: asString(group.title).trim(),
        className: asString(group.className).trim(),
        defaultOpen: typeof group.defaultOpen === 'boolean' ? group.defaultOpen : true,
        cards,
      };
    })
    .filter(Boolean);
}

function normalizeInsightsMeta(metaIn, prevInsights) {
  const meta = metaIn && typeof metaIn === 'object' ? metaIn : {};

  const prevKey =
    typeof prevInsights?.lastMilestoneKey === 'string' ? prevInsights.lastMilestoneKey : '';
  const nextKey = milestoneKey(meta.milestone);

  let milestoneBadge = '';
  if (nextKey && !prevKey) milestoneBadge = 'NEW';
  else if (nextKey && prevKey && nextKey !== prevKey) milestoneBadge = 'UPDATED';

  return {
    meta: {
      progress:
        meta.progress && typeof meta.progress === 'object'
          ? {
              answered: Number(meta.progress.answered ?? 0),
              total: Number(meta.progress.total ?? 0),
              ratio: Number(meta.progress.ratio ?? 0),
            }
          : {
              answered: 0,
              total: 0,
              ratio: 0,
            },

      confidence:
        meta.confidence && typeof meta.confidence === 'object'
          ? {
              level: asString(meta.confidence.level),
            }
          : {
              level: '',
            },

      milestone: meta.milestone && typeof meta.milestone === 'object' ? meta.milestone : null,
      milestoneBadge,
      request_id: meta.request_id ?? null,
      progressPopover:
        meta.progressPopover && typeof meta.progressPopover === 'object'
          ? {
              enabled: Boolean(meta.progressPopover.enabled),
              elements: asArray(meta.progressPopover.elements),
            }
          : {
              enabled: false,
              elements: [],
            },
    },
    lastMilestoneKey: nextKey || prevKey,
  };
}

function buildLegacyGroupsFromRailEntries(railInsights, railAssistants) {
  const groups = [];

  if (railInsights.length) {
    groups.push({
      id: 'rail-insights',
      type: 'plain',
      title: '',
      className: '',
      defaultOpen: true,
      cards: railInsights.flatMap((entry) => entry.blocks),
    });
  }

  if (railAssistants.length) {
    groups.push({
      id: 'rail-assistant',
      type: 'plain',
      title: '',
      className: 'dv-insights-group--assistant',
      defaultOpen: true,
      cards: railAssistants.flatMap((entry) => entry.blocks),
    });
  }

  return groups.filter((group) => Array.isArray(group.cards) && group.cards.length > 0);
}

function normalizeProgressPopover(progressPopoverIn) {
  const progressPopover = asObject(progressPopoverIn);

  return {
    enabled: Boolean(progressPopover.enabled),
    elements: asArray(progressPopover.elements)
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }

        const value = asString(item.value).trim();
        if (!value) {
          return null;
        }

        const hasLabel = Object.prototype.hasOwnProperty.call(item, 'label');
        const type = asString(item.type).trim() || 'text';
        const className = asString(item.className).trim();

        const normalized = {
          value,
          type,
        };

        if (hasLabel) {
          normalized.label = asString(item.label);
        }

        if (className) {
          normalized.className = className;
        }

        return normalized;
      })
      .filter(Boolean),
  };
}

function normalizeInsightsPayload(payload, prevInsights) {
  const safePayload = asObject(payload);

  const zonesIn = asObject(safePayload.zones);

  const railSource = asArray(zonesIn.rail).length
    ? zonesIn.rail
    : asArray(safePayload.railEntries).length
      ? safePayload.railEntries
      : safePayload.rail;

  const stepSource = asArray(zonesIn.steps).length
    ? zonesIn.steps
    : asArray(safePayload.stepEntries).length
      ? safePayload.stepEntries
      : safePayload.steps;

  const entriesSource = asArray(safePayload.entries).length
    ? safePayload.entries
    : [...asArray(railSource), ...asArray(stepSource)];

  const railEntries = normalizeEntries(railSource, 'rail');
  const stepEntries = normalizeEntries(stepSource, 'steps');
  const entries = normalizeEntries(entriesSource, '');

  const railInsights = railEntries.filter((entry) => entry.role === 'insight');
  const railAssistants = railEntries.filter(
    (entry) => entry.role === 'assistant' || entry.role === 'coach'
  );
  const railReports = railEntries.filter((entry) => entry.role === 'report');

  const assistant =
    safePayload.assistant && typeof safePayload.assistant === 'object'
      ? safePayload.assistant
      : {
          requested: null,
          active: {
            id: null,
            source: 'none',
          },
        };

  const pilotIdentity = safePayload.pilotIdentity ?? safePayload.meta?.pilotIdentity ?? null;

  const diagnostics =
    safePayload.diagnostics && typeof safePayload.diagnostics === 'object'
      ? safePayload.diagnostics
      : {};

  const updatedAt = safePayload.updatedAt ?? safePayload.updated_at ?? null;
  const normalizedMeta = normalizeInsightsMeta(safePayload.meta, prevInsights);

  const progressPopover = normalizeProgressPopover(
    safePayload.progressPopover ?? normalizedMeta.meta.progressPopover
  );

  const legacyGroups =
    normalizeLegacyGroups(safePayload.groups).length > 0
      ? normalizeLegacyGroups(safePayload.groups)
      : buildLegacyGroupsFromRailEntries(railInsights, railAssistants);

  return {
    zones: {
      rail: railEntries,
      steps: stepEntries,
    },
    rail: railEntries,
    steps: stepEntries,
    entries,
    railEntries,
    stepEntries,
    railInsights,
    railAssistants,
    railReports,
    groups: legacyGroups,
    assistant,
    pilotIdentity,
    diagnostics,
    progressPopover,
    raw: safePayload.raw ?? null,
    updatedAt,
    meta: {
      ...normalizedMeta.meta,
      progressPopover,
    },
    lastMilestoneKey: normalizedMeta.lastMilestoneKey,
  };
}

function normalizeCoachContext(payload) {
  const safe = asObject(payload);

  const activeIn = asObject(safe.active);
  const availableIn = asArray(safe.available);

  return {
    requested: asString(safe.requested).trim() || null,
    active: {
      id: asString(activeIn.id).trim() || null,
      label: asString(activeIn.label).trim(),
      source: asString(activeIn.source).trim() || 'none',
    },
    available: availableIn
      .map((item) => {
        const coach = asObject(item);
        if (!coach) return null;

        const id = asString(coach.id).trim();
        if (!id) return null;

        return {
          id,
          label: asString(coach.label).trim() || id,
        };
      })
      .filter(Boolean),
    fallbackUsed: Boolean(safe.fallbackUsed ?? safe.fallback_used ?? false),
  };
}

function normalizeOverviewHelpPayload(payload) {
  const safe = asObject(payload);
  const rail = normalizeEntries(asArray(safe.rail), 'rail');
  const assistant = normalizeCoachContext(safe.assistant);

  return {
    isLoading: false,
    error: '',
    rail,
    assistant,
    updatedAt: safe.updated_at ?? safe.updatedAt ?? null,
  };
}

export function blueprintReducer(state, action) {
  switch (action?.type) {
    case ACTIONS.SET_ROUTE: {
      return {
        ...state,
        route: action?.payload?.route ?? state.route,
      };
    }

    case ACTIONS.SET_CONFIG: {
      return {
        ...state,
        config: action?.payload?.config ?? state.config,
      };
    }

    case ACTIONS.SET_PACK: {
      return {
        ...state,
        packId: action?.payload?.packId ?? state.packId,
      };
    }

    case ACTIONS.SET_LOADING: {
      return {
        ...state,
        isLoading: Boolean(action?.payload?.isLoading),
      };
    }

    case ACTIONS.SET_ERROR: {
      return {
        ...state,
        error: String(action?.payload?.error || ''),
      };
    }

    case ACTIONS.SET_BLUEPRINT: {
      return {
        ...state,
        blueprint: action?.payload?.blueprint ?? state.blueprint,
      };
    }

    case ACTIONS.SET_ACTIVE_MODULE_DOC: {
      return {
        ...state,
        activeModuleDoc: action?.payload?.moduleDoc ?? null,
        activeContentIndex: 0,
        explanationMode: false,
      };
    }

    case ACTIONS.OPEN_MODULE: {
      const moduleId = action?.payload?.moduleId ?? null;
      const trackId = action?.payload?.trackId ?? null;

      return {
        ...state,
        route: ROUTES.MODULE,
        activeModuleId: moduleId,
        activeTrackId: trackId,
        activeModuleDoc: null,
        activeContentIndex: 0,
        explanationMode: false,
      };
    }

    case ACTIONS.CLOSE_MODULE: {
      return {
        ...state,
        route: ROUTES.OVERVIEW,
        activeModuleId: null,
        activeTrackId: null,
        activeModuleDoc: null,
        activeContentIndex: 0,
        explanationMode: false,
      };
    }

    case ACTIONS.NEXT_CONTENT: {
      const count = getActiveContentCount(state);
      const maxIndex = Math.max(0, count - 1);
      const nextIndex = clampInt(state?.activeContentIndex ?? 0, 0, maxIndex) + 1;

      return {
        ...state,
        activeContentIndex: clampInt(nextIndex, 0, maxIndex),
      };
    }

    case ACTIONS.PREV_CONTENT: {
      const count = getActiveContentCount(state);
      const maxIndex = Math.max(0, count - 1);
      const prevIndex = clampInt(state?.activeContentIndex ?? 0, 0, maxIndex) - 1;

      return {
        ...state,
        activeContentIndex: clampInt(prevIndex, 0, maxIndex),
      };
    }

    case ACTIONS.GOTO_CONTENT: {
      const count = getActiveContentCount(state);
      const maxIndex = Math.max(0, count - 1);
      const idx = clampInt(action?.payload?.index ?? 0, 0, maxIndex);

      return {
        ...state,
        activeContentIndex: idx,
      };
    }

    case ACTIONS.TOGGLE_EXPLANATION_MODE: {
      return {
        ...state,
        explanationMode: !state.explanationMode,
      };
    }

    case ACTIONS.SET_SELECTED_COACH: {
      const nextCoachId = asString(action?.payload?.coachId).trim();

      if (!nextCoachId) {
        return state;
      }

      return {
        ...state,
        selectedCoachId: nextCoachId,
      };
    }

    case ACTIONS.SET_SELECTED_TYPING_MODE: {
      const nextTypingMode = asString(action?.payload?.typingMode).trim();

      if (!nextTypingMode) {
        return state;
      }

      return {
        ...state,
        selectedTypingMode: nextTypingMode,
      };
    }

    case ACTIONS.SET_COACH_CONTEXT: {
      return {
        ...state,
        coachContext: normalizeCoachContext(action?.payload?.coachContext),
      };
    }

    case ACTIONS.OVERVIEW_HELP_LOADING: {
      return {
        ...state,
        overviewHelp: {
          ...(state?.overviewHelp || {}),
          isLoading: true,
          error: '',
        },
      };
    }

    case ACTIONS.OVERVIEW_HELP_SUCCESS: {
      return {
        ...state,
        overviewHelp: normalizeOverviewHelpPayload(action?.payload),
      };
    }

    case ACTIONS.OVERVIEW_HELP_ERROR: {
      return {
        ...state,
        overviewHelp: {
          ...(state?.overviewHelp || {}),
          isLoading: false,
          error: asString(action?.payload?.error).trim() || 'Failed to load overview help.',
        },
      };
    }

    case ACTIONS.SET_ANSWER: {
      const stepId = String(action?.payload?.stepId ?? '').trim();
      const value = action?.payload?.value;

      if (!stepId) {
        return state;
      }

      const explicitAnswerKey = String(action?.payload?.answerKey ?? '').trim();
      const packId = action?.payload?.packId ?? state?.packId ?? '';
      const moduleId =
        action?.payload?.moduleId ?? state?.activeModuleDoc?.id ?? state?.activeModuleId ?? '';
      const trackId = action?.payload?.trackId ?? state?.activeTrackId ?? '';
      const answerKey = explicitAnswerKey || makeAnswerKey(packId, moduleId, trackId, stepId);

      const prev = state?.answers && typeof state.answers === 'object' ? state.answers : {};

      return {
        ...state,
        answers: {
          ...prev,
          [answerKey]: value,
        },
      };
    }

    case ACTIONS.SET_ANSWERS: {
      const incoming =
        action?.payload?.answers && typeof action.payload.answers === 'object'
          ? action.payload.answers
          : {};

      return {
        ...state,
        answers: {
          ...incoming,
        },
      };
    }

    case ACTIONS.SET_CURRENT_STEP_ID: {
      const rawCurrentStepId = String(action?.payload?.currentStepId ?? '').trim();

      if (!rawCurrentStepId) {
        return state;
      }

      const steps = Array.isArray(state?.activeModuleDoc?.steps) ? state.activeModuleDoc.steps : [];
      const activeModuleId = String(
        state?.activeModuleId ?? state?.activeModuleDoc?.id ?? ''
      ).trim();

      let normalizedStepId = rawCurrentStepId;

      if (activeModuleId && normalizedStepId.startsWith(activeModuleId + '.')) {
        normalizedStepId = normalizedStepId.slice(activeModuleId.length + 1);
      }

      const idx = steps.findIndex((step) => String(step?.id ?? '').trim() === normalizedStepId);

      if (idx < 0) {
        return state;
      }

      return {
        ...state,
        activeContentIndex: idx,
      };
    }

    case ACTIONS.INSIGHTS_LOADING: {
      return {
        ...state,
        insights: {
          ...(state?.insights || {}),
          isLoading: true,
          error: '',
        },
      };
    }

    case ACTIONS.INSIGHTS_SUCCESS: {
      const prevInsights = state?.insights || {};
      const normalized = normalizeInsightsPayload(action?.payload, prevInsights);

      return {
        ...state,
        insights: {
          ...(state?.insights || {}),
          isLoading: false,
          error: '',
          zones: normalized.zones,
          rail: normalized.rail,
          steps: normalized.steps,
          entries: normalized.entries,
          railEntries: normalized.railEntries,
          stepEntries: normalized.stepEntries,
          railInsights: normalized.railInsights,
          railAssistants: normalized.railAssistants,
          railReports: normalized.railReports,
          groups: normalized.groups,
          assistant: normalized.assistant,
          pilotIdentity: normalized.pilotIdentity ?? normalized.meta?.pilotIdentity ?? null,
          diagnostics: normalized.diagnostics,
          progressPopover: normalized.progressPopover,
          raw: normalized.raw,
          meta: normalized.meta,
          lastUpdatedAt: normalized.updatedAt,
          lastMilestoneKey: normalized.lastMilestoneKey,
        },
      };
    }

    case ACTIONS.INSIGHTS_ERROR: {
      const error = String(action?.payload?.error ?? 'Failed to load insights.');
      return {
        ...state,
        insights: {
          ...(state?.insights || {}),
          isLoading: false,
          error,
        },
      };
    }

    default:
      return state ?? initialState;
  }
}
